import { useEffect, Suspense, useMemo } from 'react';
import { Route } from 'react-router-dom';
import { Navigate } from '~/utils/navigation';
import { useSelector, useDispatch } from 'react-redux';
import { AsyncPendingWithdrawals as PendingWithdrawals, Wallet } from '~/components/payment/async.js';
import { hydratePendingWithdrawals, clearPendingWithdrawal } from '~/components/payment/ducks/pendingwithdrawals';
import { selectTempAccountFlow } from '~/store/feature-toggles/onboarding-feature-toggles';
import { selectIsRegularSiteType } from '~/components/brand/ducks/index.js';
import { selectHasProducts } from '~/components/brand/ducks/selectors.js';
import useConfiguration from '~/configuration/use-configuration.js';
import { TracedRoutes } from '@comeon/error-reporter';
import NullLoader from '~/components/null-loader.js';

import {
	WeSpinDashboardRoutes as WeSpinDashboardOldFlowRoutes,
	WeSpinDashboardCreateProfile as WeSpinDashboardOldFlowCreateProfile,
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD as ROUTE_ACCOUNT_WE_SPIN_OLDFLOW_DASHBOARD,
	ROUTE_ACCOUNT_WE_SPIN_CREATE_PROFILE as ROUTE_ACCOUNT_WE_SPIN_OLDFLOW_CREATE_PROFILE
} from '~/components/we-spin/old-flow/index.js';

import {
	WeSpinDashboardRoutes as WeSpinDashboardNewFlowRoutes,
	WeSpinDashboardCreateProfile as WeSpinDashboardNewFlowCreateProfile,
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD as ROUTE_ACCOUNT_WE_SPIN_NEWFLOW_DASHBOARD,
	ROUTE_ACCOUNT_WE_SPIN_CREATE_PROFILE as ROUTE_ACCOUNT_WE_SPIN_NEWFLOW_CREATE_PROFILE
} from '~/components/we-spin/new-flow/index.js';

import {
	AccountSettingsMobile,
	AccountWithdraw,
	AccountMenu,
	AccountDeposit,
	AccountResponsibleGaming,
	AccountRealityCheckSetTimeLimit,
	TransactionHistory,
	PlayerLimitsActivityGateway,
	AccountSettingsEmail,
	AccountUpload,
	MyNotifications,
	AccountBonusCode,
	MyActivities,
	TemporaryAccountVerifications,
	SourceOfWealth,
	WebPushBackdoor,
	AccountSettings,
	AccountDepositNativeInterceptor,
	AccountRealityCheckSetDuration
} from './async.js';
// Non lazyloaded elements
import { accountHydrate } from './ducks';

import {
	ROUTE_ACCOUNT,
	ROUTE_ACCOUNT_WITHDRAW,
	ROUTE_ACCOUNT_DEPOSIT,
	ROUTE_ACCOUNT_PENDING_WITHDRAW,
	ROUTE_ACCOUNT_MY_LIMITS,
	ROUTE_ACCOUNT_TRANSACTION_HISTORY,
	ROUTE_ACCOUNT_NOTIFICATIONS,
	ROUTE_ACCOUNT_SOURCE_OF_WEALTH,
	ROUTE_ACCOUNT_WEB_PUSH_BACKDOOR,
	ROUTE_ACCOUNT_LIMITS_ACTIVITY,
	ROUTE_ACCOUNT_RISK_PROFILE,
	ROUTE_ACCOUNT_WALLET,
	ROUTE_ACCOUNT_EMAIL,
	ROUTE_ACCOUNT_UPLOAD,
	ROUTE_ACCOUNT_MYSETTINGS,
	ROUTE_ACCOUNT_PHONE,
	ROUTE_ACCOUNT_SETTINGS,
	ROUTE_ACCOUNT_BONUS_CODE,
	ROUTE_ACCOUNT_MY_ACTIVITIES,
	ROUTE_ACCOUNT_COMPLETE_VERIFICATIONS,
	ROUTE_ACCOUNT_REALITY_CHECK,
	ROUTE_ACCOUNT_DEPOSIT_INTERCEPTOR_NATIVE
} from '~/components/router/constants.js';
import RiskProfileGateway from './risk-profile/risk-profile-gateway.js';
import { getRelativePaths } from '~/utils/pathname';
import { selectWeSpinNewFlowEnabled } from '~/store/feature-toggles/we-spin-feature-toggles';

const CHILD_ROUTES = [
	ROUTE_ACCOUNT,
	ROUTE_ACCOUNT_WITHDRAW,
	ROUTE_ACCOUNT_DEPOSIT,
	ROUTE_ACCOUNT_PENDING_WITHDRAW,
	ROUTE_ACCOUNT_MY_LIMITS,
	ROUTE_ACCOUNT_REALITY_CHECK,
	ROUTE_ACCOUNT_TRANSACTION_HISTORY,
	ROUTE_ACCOUNT_NOTIFICATIONS,
	ROUTE_ACCOUNT_SOURCE_OF_WEALTH,
	ROUTE_ACCOUNT_WEB_PUSH_BACKDOOR,
	ROUTE_ACCOUNT_LIMITS_ACTIVITY,
	ROUTE_ACCOUNT_RISK_PROFILE,
	ROUTE_ACCOUNT_WALLET,
	ROUTE_ACCOUNT_EMAIL,
	ROUTE_ACCOUNT_UPLOAD,
	ROUTE_ACCOUNT_MYSETTINGS,
	ROUTE_ACCOUNT_PHONE,
	ROUTE_ACCOUNT_SETTINGS,
	ROUTE_ACCOUNT_BONUS_CODE,
	ROUTE_ACCOUNT_MY_ACTIVITIES,
	ROUTE_ACCOUNT_COMPLETE_VERIFICATIONS,
	ROUTE_ACCOUNT_WE_SPIN_OLDFLOW_DASHBOARD,
	ROUTE_ACCOUNT_WE_SPIN_OLDFLOW_CREATE_PROFILE,
	ROUTE_ACCOUNT_WE_SPIN_NEWFLOW_DASHBOARD,
	ROUTE_ACCOUNT_WE_SPIN_NEWFLOW_CREATE_PROFILE,
	ROUTE_ACCOUNT_DEPOSIT_INTERCEPTOR_NATIVE
];

const AccountGateway = () => {
	const dispatch = useDispatch();
	const { enableNoAccountWelcomeBonus, useNewRealityCheck } = useConfiguration();
	const { useMyActivities, hydrated: isAccountHydrated, isRegistration } = useSelector(state => state.account);
	const { limitsActivityEnabled, realityCheckScreenEnabled, myNotificationsEnabled, riskProfileEnabled } =
		useSelector(state => state.brand);
	const showTempAccountFlow = useSelector(selectTempAccountFlow);
	const weSpinNewFlowEnabled = useSelector(selectWeSpinNewFlowEnabled);

	/** CONDITIONS * */
	// Try to use feature toggle as much as possible, since its easier to turn ON/OFF w/o code change
	const isRegularSiteType = useSelector(selectIsRegularSiteType);

	const { hasWeSpin: weSpinEnabled } = useSelector(selectHasProducts);
	const myActivitiesEnabled = (isRegularSiteType || enableNoAccountWelcomeBonus) && useMyActivities;
	const paths = useMemo(() => getRelativePaths(CHILD_ROUTES, ROUTE_ACCOUNT), []);
	// on mount
	useEffect(() => {
		// hydrate only if account is not hydrated
		if (!isAccountHydrated && isRegistration) {
			dispatch(accountHydrate());
		}
		dispatch(hydratePendingWithdrawals());
		return () => dispatch(clearPendingWithdrawal());
	}, []); //eslint-disable-line

	return (
		<Suspense fallback={<NullLoader />}>
			<TracedRoutes>
				<Route path="/:emailVerificationStatus?" element={<AccountMenu />} />
				<Route path={`${paths[ROUTE_ACCOUNT_DEPOSIT]}/*`} element={<AccountDeposit />} />
				<Route path={paths[ROUTE_ACCOUNT_WITHDRAW]} element={<AccountWithdraw />} />
				<Route path={paths[ROUTE_ACCOUNT_PENDING_WITHDRAW]} element={<PendingWithdrawals />} />
				<Route path={`${paths[ROUTE_ACCOUNT_MY_LIMITS]}/*`} element={<AccountResponsibleGaming />} />
				<Route path={`${paths[ROUTE_ACCOUNT_PHONE]}/*`} element={<AccountSettingsMobile />} />
				<Route path={paths[ROUTE_ACCOUNT_EMAIL]} element={<AccountSettingsEmail />} />
				<Route path={`${paths[ROUTE_ACCOUNT_SETTINGS]}/*`} element={<AccountSettings />} />
				<Route path={paths[ROUTE_ACCOUNT_SOURCE_OF_WEALTH]} element={<SourceOfWealth />} />
				<Route path={paths[ROUTE_ACCOUNT_WEB_PUSH_BACKDOOR]} element={<WebPushBackdoor />} />
				<Route
					path={paths[ROUTE_ACCOUNT_DEPOSIT_INTERCEPTOR_NATIVE]}
					element={<AccountDepositNativeInterceptor />}
				/>
				{realityCheckScreenEnabled && !useNewRealityCheck && (
					<Route path={paths[ROUTE_ACCOUNT_REALITY_CHECK]} element={<AccountRealityCheckSetTimeLimit />} />
				)}
				{realityCheckScreenEnabled && useNewRealityCheck && (
					<Route path={paths[ROUTE_ACCOUNT_REALITY_CHECK]} element={<AccountRealityCheckSetDuration />} />
				)}
				<Route path={`${paths[ROUTE_ACCOUNT_TRANSACTION_HISTORY]}/*`} element={<TransactionHistory />} />
				{myNotificationsEnabled && (
					<Route
						path={`${paths[ROUTE_ACCOUNT_NOTIFICATIONS]}/:notificationId?`}
						element={<MyNotifications />}
					/>
				)}
				{riskProfileEnabled && (
					<Route path={`${paths[ROUTE_ACCOUNT_RISK_PROFILE]}/*`} element={<RiskProfileGateway />} />
				)}
				{limitsActivityEnabled && (
					<Route
						path={`${paths[ROUTE_ACCOUNT_LIMITS_ACTIVITY]}/*`}
						element={<PlayerLimitsActivityGateway />}
					/>
				)}
				{/* Is wallet  */}
				<Route path={paths[ROUTE_ACCOUNT_WALLET]} element={<Wallet />} />
				{/**
				 Using array because it doesn't break switch. Othe option is to add showRegular condition alongwith every route which
				 Should be conditionally rendered.

				 A common parent doesn't treat children routes as part of other routes so the fallback redirect breaks.
				 Using path as key because that's unique part but not necessarily key prop should be tha path.
				 */}
				{isRegularSiteType && (
					<>
						<Route path={paths[ROUTE_ACCOUNT_UPLOAD]} element={<AccountUpload />} />,
						<Route
							path={`${paths[ROUTE_ACCOUNT_MYSETTINGS]}/verify-email`}
							element={<Navigate to={ROUTE_ACCOUNT_EMAIL} />}
						/>
						,
						<Route
							path={`${paths[ROUTE_ACCOUNT_MYSETTINGS]}/verify-mobile`}
							element={<Navigate to={ROUTE_ACCOUNT_PHONE} />}
						/>
						,
						<Route path={paths[ROUTE_ACCOUNT_BONUS_CODE]} element={<AccountBonusCode />} />
					</>
				)}
				{myActivitiesEnabled && <Route path={paths[ROUTE_ACCOUNT_MY_ACTIVITIES]} element={<MyActivities />} />}
				{showTempAccountFlow && (
					<Route
						path={paths[ROUTE_ACCOUNT_COMPLETE_VERIFICATIONS]}
						element={<TemporaryAccountVerifications />}
					/>
				)}
				{/* TEMPORARY WESPIN FLOWS */}
				{weSpinEnabled && !weSpinNewFlowEnabled && (
					<>
						<Route
							path={`${paths[ROUTE_ACCOUNT_WE_SPIN_OLDFLOW_DASHBOARD]}/*`}
							element={<WeSpinDashboardOldFlowRoutes />}
						/>
						<Route
							path={paths[ROUTE_ACCOUNT_WE_SPIN_OLDFLOW_CREATE_PROFILE]}
							element={<WeSpinDashboardOldFlowCreateProfile />}
						/>
					</>
				)}
				{weSpinEnabled && weSpinNewFlowEnabled && (
					<>
						<Route
							path={`${paths[ROUTE_ACCOUNT_WE_SPIN_NEWFLOW_DASHBOARD]}/*`}
							element={<WeSpinDashboardNewFlowRoutes />}
						/>
						<Route
							path={paths[ROUTE_ACCOUNT_WE_SPIN_NEWFLOW_CREATE_PROFILE]}
							element={<WeSpinDashboardNewFlowCreateProfile />}
						/>
					</>
				)}
				<Route path="*" element={<Navigate to={ROUTE_ACCOUNT} />} />
			</TracedRoutes>
		</Suspense>
	);
};

export default AccountGateway;
